import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Doc } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { Textarea } from '@rekrytering/ui/components/textarea'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDoubleCheck } from '@/shared/hooks/use-double-check'

function QuestionBankItem({ item }: { item: Doc<'questionBank'> }) {
  const { t } = useTranslation()
  const { doubleCheck, getButtonProps } = useDoubleCheck()

  const { mutateAsync: deleteItem } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.question_bank.deleteMyQuestionBankItem,
    ),
  })

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card px-5 py-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-primary">{item.text}</p>
        {item.competency && (
          <p className="text-xs text-primary/60">{item.competency}</p>
        )}
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="shrink-0"
        aria-label={
          doubleCheck
            ? t('questionBank.deleteConfirm')
            : t('questionBank.delete')
        }
        {...getButtonProps({
          onClick: doubleCheck
            ? () => deleteItem({ questionBankItemId: item._id })
            : undefined,
        })}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

export function QuestionBankPage() {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [competency, setCompetency] = useState('')

  const { data: items = [] } = useQuery(
    convexQuery(api.features.jobs.question_bank.listMyQuestionBankItems, {}),
  )

  const { mutateAsync: saveQuestion, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.question_bank.saveMyQuestionToBank,
    ),
  })

  const handleAddQuestion = async () => {
    const trimmedText = text.trim()
    if (!trimmedText) return

    await saveQuestion({
      text: trimmedText,
      competency: competency.trim() || undefined,
    })

    setText('')
    setCompetency('')
  }

  return (
    <main className="flex flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-(--breakpoint-xl) flex-col gap-8 px-6 py-8">
        <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6">
          <h2 className="text-base font-medium text-primary">
            {t('questionBank.addQuestion')}
          </h2>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('questionBank.textPlaceholder')}
            rows={3}
          />
          <Input
            value={competency}
            onChange={(e) => setCompetency(e.target.value)}
            placeholder={t('questionBank.competencyPlaceholder')}
          />
          <div className="flex justify-end">
            <Button
              onClick={handleAddQuestion}
              disabled={!text.trim() || isPending}
            >
              {t('questionBank.save')}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {items.length === 0 ? (
            <p className="text-sm text-primary/60">{t('questionBank.empty')}</p>
          ) : (
            items.map((item) => <QuestionBankItem key={item._id} item={item} />)
          )}
        </div>
      </div>
    </main>
  )
}
